import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCalendar, FiUser, FiPhone, FiMapPin, FiHome, FiSettings, FiMessageSquare, FiImage } = FiIcons;

const SegnalazioneDetail = ({ segnalazione, users, onClose }) => {
  const { isDark } = useTheme();
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
  const segnalatore = users.find(u => u.id === segnalazione.user_id);

  const getStatusColor = (status) => {
    const colors = {
      'In attesa': isDark ? 'bg-yellow-800 text-yellow-200' : 'bg-yellow-100 text-yellow-800',
      'Visionato': isDark ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800',
      'Incarico preso': isDark ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800',
      'Incarico non preso': isDark ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-800',
      'Venduto': isDark ? 'bg-purple-800 text-purple-200' : 'bg-purple-100 text-purple-800',
      'Rifiutato': isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
    };
    return colors[status] || (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className={`${
        isDark ? 'bg-gray-800' : 'bg-white'
      } rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl`}>
        
        {/* Header */}
        <div className={`sticky top-0 ${
          isDark 
            ? 'bg-gradient-to-r from-blue-800 to-blue-900' 
            : 'bg-gradient-to-r from-blue-600 to-blue-700'
        } text-white p-6 rounded-t-2xl`}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-2">Dettagli Segnalazione</h2>
              <span className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(segnalazione.stato_segnalazione)}`}>
                {segnalazione.stato_segnalazione}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Foto Gallery */}
          {segnalazione.images && segnalazione.images.length > 0 && (
            <div className="mb-8">
              <h3 className={`text-lg font-bold mb-4 flex items-center ${
                isDark ? 'text-white' : 'text-gray-800'
              }`}>
                <SafeIcon icon={FiImage} className="w-5 h-5 mr-2" />
                Foto Immobile ({segnalazione.images.length})
              </h3>
              
              {/* Main Image */}
              <div className="mb-4">
                <img
                  src={segnalazione.images[selectedImageIndex]?.url}
                  alt={`Foto ${selectedImageIndex + 1}`}
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
              </div>
              
              {/* Thumbnails */}
              {segnalazione.images.length > 1 && (
                <div className="flex space-x-3">
                  {segnalazione.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index
                          ? 'border-blue-500 scale-105'
                          : isDark ? 'border-gray-600' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={img.url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-20 h-20 object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Informazioni */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Dettagli Immobile */}
            <div className="space-y-6">
              <h3 className={`text-xl font-bold flex items-center ${
                isDark ? 'text-white' : 'text-gray-800'
              }`}>
                <SafeIcon icon={FiHome} className="w-5 h-5 mr-2" />
                Dettagli Immobile
              </h3>

              <div className="space-y-4">
                <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiMapPin} className={`w-5 h-5 mt-1 ${
                      isDark ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <div>
                      <label className={`block text-sm font-bold mb-1 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Indirizzo
                      </label>
                      <p className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg`}>
                        {segnalazione.indirizzo}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <label className={`block text-sm font-bold mb-1 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Metri Quadri
                    </label>
                    <p className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>
                      {segnalazione.mq} mq
                    </p>
                  </div>

                  <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <label className={`block text-sm font-bold mb-1 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Tipo
                    </label>
                    <p className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>
                      {segnalazione.tipo_immobile}
                    </p>
                  </div>
                </div>

                <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiSettings} className={`w-5 h-5 mt-1 ${
                      isDark ? 'text-green-400' : 'text-green-600'
                    }`} />
                    <div>
                      <label className={`block text-sm font-bold mb-1 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Stato Immobile
                      </label>
                      <p className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg`}>
                        {segnalazione.stato_immobile}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contatti e Info */}
            <div className="space-y-6">
              <h3 className={`text-xl font-bold flex items-center ${
                isDark ? 'text-white' : 'text-gray-800'
              }`}>
                <SafeIcon icon={FiUser} className="w-5 h-5 mr-2" />
                Informazioni Contatto
              </h3>

              <div className="space-y-4">
                <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <label className={`block text-sm font-bold mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Proprietario
                  </label>
                  <p className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg font-semibold mb-2`}>
                    {segnalazione.nome_proprietario}
                  </p>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiPhone} className={`w-4 h-4 ${
                      isDark ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <span className={`${isDark ? 'text-blue-300' : 'text-blue-600'} font-medium`}>
                      {segnalazione.telefono_proprietario}
                    </span>
                  </div>
                </div>

                {segnalatore && (
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <label className={`block text-sm font-bold mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Segnalatore
                    </label>
                    <p className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg font-semibold mb-2`}>
                      {segnalatore.email}
                    </p>
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiPhone} className={`w-4 h-4 ${
                        isDark ? 'text-blue-400' : 'text-blue-600'
                      }`} />
                      <span className={`${isDark ? 'text-blue-300' : 'text-blue-600'} font-medium`}>
                        {segnalatore.phone_number}
                      </span>
                    </div>
                  </div>
                )}

                <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center space-x-3 mb-2">
                    <SafeIcon icon={FiCalendar} className={`w-4 h-4 ${
                      isDark ? 'text-purple-400' : 'text-purple-600'
                    }`} />
                    <label className={`text-sm font-bold ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Data Creazione
                    </label>
                  </div>
                  <p className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>
                    {new Date(segnalazione.created_at).toLocaleDateString('it-IT')} alle {new Date(segnalazione.created_at).toLocaleTimeString('it-IT')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Note */}
          {(segnalazione.note || segnalazione.note_admin) && (
            <div className="mt-8 space-y-6">
              <h3 className={`text-xl font-bold flex items-center ${
                isDark ? 'text-white' : 'text-gray-800'
              }`}>
                <SafeIcon icon={FiMessageSquare} className="w-5 h-5 mr-2" />
                Note e Comunicazioni
              </h3>

              {segnalazione.note && (
                <div className={`p-4 rounded-xl border-l-4 ${
                  isDark 
                    ? 'bg-gray-700 border-gray-500' 
                    : 'bg-gray-50 border-gray-400'
                }`}>
                  <label className={`block text-sm font-bold mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    📝 Note del segnalatore:
                  </label>
                  <p className={`${isDark ? 'text-gray-200' : 'text-gray-800'} leading-relaxed`}>
                    {segnalazione.note}
                  </p>
                </div>
              )}

              {segnalazione.note_admin && (
                <div className={`p-4 rounded-xl border-l-4 ${
                  isDark 
                    ? 'bg-blue-900 border-blue-500' 
                    : 'bg-blue-50 border-blue-400'
                }`}>
                  <label className={`block text-sm font-bold mb-2 ${
                    isDark ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    💬 Note dell'agenzia:
                  </label>
                  <p className={`${isDark ? 'text-blue-200' : 'text-blue-800'} leading-relaxed`}>
                    {segnalazione.note_admin}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Close Button */}
          <div className="mt-8 flex justify-end">
            <button
              onClick={onClose}
              className={`px-8 py-3 rounded-lg font-semibold transition-colors ${
                isDark
                  ? 'bg-gray-600 text-white hover:bg-gray-500'
                  : 'bg-gray-500 text-white hover:bg-gray-600'
              }`}
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegnalazioneDetail;